import { ProductHelperFactory } from '@services/MarppleShop/features/ProductHelper/strategies';
import { ProductBadgeFactory } from '@services/MarppleShop/renderApp/components/cells/ProductBadge/ProductBadgeFactory.ts';
import { ProductBadgeListData } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/ProductBadgeList.ts';
import { ProductBadgeHelper } from '@services/MarppleShop/renderApp/components/cells/ProductBadge/ProductBadgeHelper.ts';
import {
  ContextPageType,
  IProductBadgeStrategy,
  MpsV1BadgeDefinition,
} from './product-badge-strategy.interface';
import { isDate } from '@fxts/core';
import { ProductBadgeType } from '@services/MarppleShop/features/ProductBadge/type';

export class ProductBadgeStrategyMpsV1 implements IProductBadgeStrategy<MpsV1BadgeDefinition> {
  private readonly productHelper = ProductHelperFactory.createMpsV1();

  // 이 배열은 순서에 관여하지 않습니다.
  getBadgeDefinitions(): MpsV1BadgeDefinition[] {
    return [
      this.getSoldOut(),
      this.getMembership(),
      this.getUpcoming(),
      this.getTrending(),
      this.getPopular(),
      this.getBrandDeal(),
      this.getLimited(),
      this.getQuantityLimited(),
      this.getPeriodLimited(),
      this.getNft(),
    ];
  }

  private getSoldOut(): MpsV1BadgeDefinition {
    return {
      type: ProductBadgeType.SOLD_OUT,
      priority: 1,
      shouldShow: (data, pageContext) => {
        return (
          this.productHelper.isSoldOut(data) &&
          !this.productHelper.isBeforeSale(data.sell_start_at) &&
          !this.productHelper.isFunding(data.is_funding) &&
          pageContext.pageType !== ContextPageType.LIST
        );
      },
      createBadge: (data, option) => {
        return ProductBadgeFactory.create(ProductBadgeType.SOLD_OUT, {
          value: undefined,
          option,
          server_timestamp: data.server_timestamp,
        });
      },
    };
  }

  private getMembership(): MpsV1BadgeDefinition {
    return {
      type: ProductBadgeType.MEMBERSHIP,
      priority: 2,
      shouldShow: (data) => this.productHelper.isMembership(data.is_membership),
      createBadge: (data, option, pageContext) => {
        return ProductBadgeFactory.create(ProductBadgeType.MEMBERSHIP, {
          value: undefined,
          option: {
            ...option,
            hide_label: pageContext.pageType !== ContextPageType.DETAIL,
          },
          server_timestamp: data.server_timestamp,
        });
      },
    };
  }

  private getTrending(): MpsV1BadgeDefinition {
    return {
      type: ProductBadgeType.TRENDING,
      priority: 3,
      shouldShow: (data) => this.productHelper.isTrending(data.is_trending),
      createBadge: (data, option) => {
        return ProductBadgeFactory.create(ProductBadgeType.TRENDING, {
          value: undefined,
          option,
          server_timestamp: data.server_timestamp,
        });
      },
    };
  }

  private getPopular(): MpsV1BadgeDefinition {
    return {
      type: ProductBadgeType.POPULAR,
      priority: 3,
      shouldShow: (data) => {
        // 인기 AND (NOT 급상승)
        return (
          this.productHelper.isPopular(data.is_popular) && !this.productHelper.isTrending(data.is_trending)
        );
      },
      createBadge: (data, option) => {
        return ProductBadgeFactory.create(ProductBadgeType.POPULAR, {
          value: undefined,
          option,
          server_timestamp: data.server_timestamp,
        });
      },
    };
  }

  private getBrandDeal(): MpsV1BadgeDefinition {
    return {
      type: ProductBadgeType.BRAND_DEAL,
      priority: 4,
      shouldShow: (data) => {
        return this.productHelper.isBrandDeal(data.is_brand_deal);
      },
      createBadge: (data, option) => {
        return ProductBadgeFactory.create(ProductBadgeType.BRAND_DEAL, {
          value: undefined,
          option,
          server_timestamp: data.server_timestamp,
        });
      },
    };
  }

  private getLimited(): MpsV1BadgeDefinition {
    return {
      type: ProductBadgeType.LIMITED,
      priority: 5,
      shouldShow: (data) => {
        return (
          this.productHelper.isLimited(data) &&
          !this.productHelper.isFunding(data.is_funding) &&
          !this.productHelper.isBeforeSale(data.sell_start_at)
        );
      },
      createBadge: (data, option) =>
        ProductBadgeFactory.create(ProductBadgeType.LIMITED, {
          value: undefined,
          option,
          server_timestamp: data.server_timestamp,
        }),
    };
  }

  private getQuantityLimited(): MpsV1BadgeDefinition {
    return {
      type: ProductBadgeType.QUANTITY_LIMITED,
      priority: 6,
      shouldShow: (data) => {
        return this.shouldShowQuantityLimited(data);
      },
      createBadge: (data, option) => {
        const remaining_stock = data.quantity - data.order_count;
        return ProductBadgeFactory.create(ProductBadgeType.QUANTITY_LIMITED, {
          value: remaining_stock,
          option,
          server_timestamp: data.server_timestamp,
        });
      },
    };
  }

  private getPeriodLimited(): MpsV1BadgeDefinition {
    return {
      type: ProductBadgeType.PERIOD_LIMITED,
      priority: 6,
      shouldShow: (data) => {
        return this.shouldShowPeriodLimited(data);
      },
      createBadge: (data, option) => {
        if (!isDate(data.sell_end_at)) {
          throw new Error('sell_end_at is required.');
        }

        return ProductBadgeFactory.create(ProductBadgeType.PERIOD_LIMITED, {
          value: data.sell_end_at,
          option,
          server_timestamp: data.server_timestamp,
        });
      },
    };
  }

  private getNft(): MpsV1BadgeDefinition {
    return {
      type: ProductBadgeType.NFT,
      priority: 7,
      shouldShow: (data) => {
        return !!data.is_token_gate;
      },
      createBadge: (data, option) =>
        ProductBadgeFactory.create(ProductBadgeType.NFT, {
          value: undefined,
          option,
          server_timestamp: data.server_timestamp,
        }),
    };
  }

  private getUpcoming(): MpsV1BadgeDefinition {
    return {
      type: ProductBadgeType.UPCOMING,
      priority: 8,
      shouldShow: (data) => this.productHelper.isBeforeSale(data.sell_start_at),
      createBadge: (data, option) => {
        if (!this.productHelper.isBeforeSale(data.sell_start_at)) {
          throw new Error('sell_start_at is required');
        }

        return ProductBadgeFactory.create(ProductBadgeType.UPCOMING, {
          value: data.sell_start_at,
          option,
          server_timestamp: data.server_timestamp,
        });
      },
    };
  }

  /**
   * 수량한정 뱃지 표시 여부 판단
   */
  private shouldShowQuantityLimited(data: ProductBadgeListData): boolean {
    if (
      !this.productHelper.isLimited(data) ||
      this.productHelper.isBeforeSale(data.sell_start_at) ||
      this.productHelper.isBrandDeal(data.is_brand_deal)
    ) {
      return false;
    }

    // 기간한정과 수량한정이 모두 있는 경우 우선순위 계산
    if (ProductBadgeHelper.isBothQuantityAndPeriodLimitedBadgeVisible(data) && data.sell_end_at) {
      const remaining_stock = data.quantity - data.order_count;
      const period_priority = ProductBadgeHelper.getPeriodLimitedBadgePriority(data.sell_end_at);
      const quantity_priority = ProductBadgeHelper.getQuantityLimitedBadgePriority(remaining_stock);

      // 수량한정이 더 높은 우선순위일 때만 표시
      return quantity_priority <= period_priority;
    }

    // 수량한정만 있는 경우
    return ProductBadgeHelper.isQuantityLimitedBadgeVisible(data);
  }

  /**
   * 기간한정 뱃지 표시 여부 판단
   */
  private shouldShowPeriodLimited(data: ProductBadgeListData): boolean {
    if (
      !this.productHelper.isLimited(data) ||
      this.productHelper.isBeforeSale(data.sell_start_at) ||
      !data.sell_end_at ||
      this.productHelper.isBrandDeal(data.is_brand_deal)
    ) {
      return false;
    }

    // 기간한정과 수량한정이 모두 있는 경우 우선순위 계산
    if (ProductBadgeHelper.isBothQuantityAndPeriodLimitedBadgeVisible(data)) {
      const remaining_stock = data.quantity - data.order_count;
      const period_priority = ProductBadgeHelper.getPeriodLimitedBadgePriority(data.sell_end_at);
      const quantity_priority = ProductBadgeHelper.getQuantityLimitedBadgePriority(remaining_stock);

      // 기간한정이 더 높은 우선순위일 때만 표시
      return period_priority < quantity_priority;
    }

    // 기간한정만 있는 경우
    return ProductBadgeHelper.isPeriodLimitedBadgeVisible(data);
  }
}
